import {
  Box,
  Button,
  FormHelperText,
  TextField,
  Typography,
} from "@mui/material";
import { Form, Formik, FormikHelpers } from "formik";
import { useRouter } from "next/router";
import { useSnackbar } from "notistack";
import * as React from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { FormikTextField } from "components/form/FormikTextField";

import { ROUTES } from "constants/routes";
import useAuth from "hooks/useAuth";
import useIsMountedRef from "hooks/useIsMountedRef";

interface VerifyCodeFormState {
  email: string;
  code: string[];
  submit?: string;
}

const verifyCodeFormSchema: Yup.SchemaOf<VerifyCodeFormState> = Yup.object({
  email: Yup.string()
    .email("Must be a valid email")
    .max(255)
    .required("Email is required"),
  code: Yup.array().of(Yup.string().required("Code is required")),
  submit: Yup.string(),
});

export const VerifyCodeForm: React.FC<{
  loginValues: { email: string; password: string };
}> = ({ loginValues }) => {
  const isMountedRef = useIsMountedRef();
  const { t } = useTranslation();
  const itemsRef = React.useRef<HTMLInputElement[]>([]);
  const router = useRouter();
  const { confirmCode, login } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const initialValues = {
    email: loginValues.email,
    code: ["", "", "", "", "", ""],
  };

  React.useEffect(() => {
    itemsRef.current = itemsRef.current.slice(0, 6);
  }, []);

  const handleVerifyCodeSubmit = async (
    values: VerifyCodeFormState,
    { setErrors, setSubmitting }: FormikHelpers<VerifyCodeFormState>
  ) => {
    try {
      await confirmCode({
        code: values.code,
        email: values.email,
      });

      enqueueSnackbar("Account Verified", {
        variant: "success",
      });

      await login({
        email: loginValues.email,
        password: loginValues.password,
      });
      router.push(ROUTES.PLAN_SELECTION);
    } catch (err: unknown) {
      if (isMountedRef.current) {
        setErrors({ submit: "Code verification failed" });
        setSubmitting(false);
      }
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={verifyCodeFormSchema}
      onSubmit={handleVerifyCodeSubmit}
    >
      {({
        errors,
        handleBlur,
        isSubmitting,
        setFieldValue,
        touched,
        values,
      }) => (
        <Form>
          <FormikTextField
            autoFocus
            fullWidth
            label={t("verify_code.email")}
            margin="normal"
            name="email"
            type="email"
            value={values.email}
            disabled
          />

          <Typography
            color="textSecondary"
            sx={{
              mb: 2,
              mt: 3,
            }}
            variant="subtitle2"
          >
            {t("verify_code.verification_code")}
          </Typography>

          <Box
            sx={{
              display: "grid",
              columnGap: "16px",
              gridTemplateColumns: "repeat(7, 1fr)",
              pt: 1,
            }}
          >
            {[1, 2, 3, 4, 5, 6].map((ref, i) => (
              <TextField
                error={Boolean(
                  Array.isArray(touched.code) &&
                    touched.code.length === 6 &&
                    errors.code
                )}
                fullWidth
                inputRef={(el: HTMLInputElement) => {
                  itemsRef.current[i] = el;
                }}
                key={`code-${i}`}
                name={`code[${i}]`}
                onBlur={handleBlur}
                onKeyDown={(event) => {
                  if (event.code === "ENTER") {
                    if (values.code[i]) {
                      setFieldValue(`code[${i}]`, "");
                      return;
                    }

                    if (i > 0) {
                      setFieldValue(`code[${i}]`, "");
                      itemsRef.current[i - 1].focus();
                      return;
                    }
                  }

                  if (Number.isInteger(parseInt(event.key, 10))) {
                    setFieldValue(`code[${i}]`, event.key);

                    if (i < 5) {
                      itemsRef.current[i + 1].focus();
                    }
                  }
                }}
                onPaste={(event) => {
                  const paste = event.clipboardData.getData("text");
                  const pasteArray = paste.split("");

                  if (pasteArray.length !== 6) {
                    return;
                  }

                  const valid = pasteArray.every((x) =>
                    Number.isInteger(parseInt(x, 10))
                  );

                  if (valid) {
                    setFieldValue("code", pasteArray);
                    itemsRef.current[5].focus();
                  }
                }}
                value={values.code[i]}
                sx={{
                  display: "inline-block",
                  textAlign: "center",
                  "& .MuiInputBase-input": {
                    textAlign: "center",
                  },
                }}
                variant="outlined"
              />
            ))}
          </Box>

          {Array.isArray(touched.code) &&
            touched.code.length === 6 &&
            Boolean(errors.code) && (
              <FormHelperText error sx={{ mx: "14px" }}>
                {Array.isArray(errors.code) && errors.code[0]}
              </FormHelperText>
            )}

          {errors.submit && (
            <Box sx={{ mt: 3 }}>
              <FormHelperText error>{errors.submit}</FormHelperText>
            </Box>
          )}

          <Box sx={{ mt: 3 }}>
            <Button
              color="secondary"
              disabled={isSubmitting}
              fullWidth
              size="large"
              type="submit"
              variant="contained"
            >
              {t("verify_code.verify")}
            </Button>
          </Box>
        </Form>
      )}
    </Formik>
  );
};
